
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useI18n } from '../../i18n';
import { Checkbox, Icon } from '../../ui';
import type { TaskStart } from './TaskDialog';

// After a call started from the app (TF-45): a bar offering to log it, and the form that does. The
// app does not know whether the call happened or how it went, so nothing is written unless the user
// presses Log call.

// The provider and its bar are in every company page; the forms, only once a call is logged.
const LogCallDialog = lazy(() => import('./LogCallDialog').then((module) => ({ default: module.LogCallDialog })));
const TaskDialog = lazy(() => import('./TaskDialog').then((module) => ({ default: module.TaskDialog })));

/** Who was called, and the records the logged call is filed under. */
export interface CallTarget {
  name: string;
  contactId?: string | null;
  accountId?: string | null;
  dealId?: string | null;
}

/** How long the bar stays once shown. */
export const CALL_BAR_MS = 2 * 60_000;
/** How long a phone waits for the user to come back from calling before the offer lapses. */
const CALL_WAIT_MS = 60 * 60_000;

// The personal switch, remembered in this browser like the language and the theme: on a computer
// someone may log calls elsewhere, and still want the offer on their phone.
const STORAGE_KEY = 'teamfolio.callPrompt';
const listeners = new Set<() => void>();

function promptOn(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

function setPromptOn(on: boolean): void {
  try {
    if (on) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, 'off');
  } catch {
    // Private windows may refuse; the choice then lasts until the page is closed.
  }
  listeners.forEach((listener) => listener());
}

function useCallPrompt(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    promptOn,
    () => true,
  );
}

/** The switch in the user's own settings. */
export function CallPromptSwitch({ id }: { id: string }) {
  const { t } = useI18n();
  const on = useCallPrompt();
  return (
    <Checkbox id={id} label={t('account.callPrompt')} checked={on} onChange={setPromptOn} />
  );
}

const CallStarted = createContext<(tenantId: string, target: CallTarget) => void>(() => undefined);

/** For a call button: tell the app a call to this person is starting. */
export const useCallStarted = () => useContext(CallStarted);

interface Pending {
  tenantId: string;
  target: CallTarget;
  /** The page the call started on: going to another record ends the offer. */
  path: string;
  shown: boolean;
}

/** Around the pages of a company: the bar and the form, for every call button inside. */
export function CallLogProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [pending, setPending] = useState<Pending | null>(null);
  const [logging, setLogging] = useState<Pick<Pending, 'tenantId' | 'target'> | null>(null);
  // A follow-up task from the call just logged (TF-52): the dialog opens once the call form has closed.
  const [followUp, setFollowUp] = useState<{ tenantId: string; start: TaskStart } | null>(null);

  const started = useCallback(
    (tenantId: string, target: CallTarget) => {
      // Read at the tap, so the call buttons need not re-render when the page or the switch changes.
      if (!promptOn()) return;
      // A phone leaves the app for its dialer: offer when the user comes back. On a computer the
      // calling app opens beside the browser: offer straight away.
      const phone = window.matchMedia('(pointer: coarse)').matches;
      setPending({ tenantId, target, path: window.location.pathname, shown: !phone });
    },
    [],
  );

  useEffect(() => {
    if (!pending || pending.shown) return;
    const back = () => {
      if (document.visibilityState === 'visible') setPending((now) => (now ? { ...now, shown: true } : now));
    };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [pending]);

  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setPending(null), pending.shown ? CALL_BAR_MS : CALL_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);

  useEffect(() => setPending((now) => (now && now.path !== pathname ? null : now)), [pathname]);

  return (
    <CallStarted.Provider value={started}>
      {children}
      {pending?.shown && (
        <div className="call-bar" role="region" aria-label={t('crm.callBar')}>
          <Icon name="call" size={20} />
          <span className="grow">{t('crm.logCallQuestion', { name: pending.target.name })}</span>
          <span className="btn-row">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setLogging(pending);
                setPending(null);
              }}
            >
              {t('crm.add.call')}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setPending(null)}>
              {t('common.dismiss')}
            </button>
          </span>
        </div>
      )}
      <Suspense fallback={null}>
        {logging && (
          <LogCallDialog
            tenantId={logging.tenantId}
            target={logging.target}
            onClose={(start) => {
              setLogging(null);
              if (start) setFollowUp({ tenantId: logging.tenantId, start });
            }}
          />
        )}
        {followUp && <TaskDialog tenantId={followUp.tenantId} start={followUp.start} open onOpenChange={(open) => !open && setFollowUp(null)} />}
      </Suspense>
    </CallStarted.Provider>
  );
}
